import {
  Alert,
  Backdrop,
  Box,
  Checkbox,
  CircularProgress,
  MenuItem,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material';
import { CreateOrUpdatePaymentMethodACHInput } from '@optimus/models';
import { useEffect, useState } from 'react';
import {
  FormProvider, useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  ErrorAlert,
  IBankAccountFormInputs, StyledSelect,
} from '../../../common/StyledFormInputs';
import { PaymentMethodBankAccountType, PaymentMethodStatus, PayoutDialogContentType } from '../../../common/enums';
import { getFullAddressUS, sanitizeString } from '../../../common/utils/formUtils';
import { AddressAutoCompleteInput } from '../../../common/AddressAutoCompleteInput';
import { TestIds } from '../../../assets/testIds';
import { activatePaymentMethod, getPaymentMethods } from '../../../store/thunks/dashboardThunk';
import { selectBankAccount, UpdateBankAccountState } from '../../../store/slices/dashboardSlice';
import { bankAccountNumberRegex, numberLength9Regex } from '../../../common/constants';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import {
  PayoutCTA,
  FieldBox, TransparentPayoutCTA, RequiredMark, StyledTextField,
} from './Common';
import { PaymentMethodType } from '../../../common/models/PaymentMethod';
import { StateAbbreviationsUS } from '../../../common/StateAbbreviationsUS';
import {
  setPaymentMethod,
} from '../../../api/dashboardAPI';
import { selectMemberInformation } from '../../../store/slices/onboardingSlice';
import { Logger } from '../../../config/logging/logger';

interface BankAccountFormProps {
  setDialogSubTitle?: (event: string | JSX.Element) => void;
  setDialogContentType?: (event: PayoutDialogContentType) => void;
  setOpenDialog?: (event: boolean) => void;
  setPaymentMethodId?: (event: string) => void;
}

export function BankAccountForm({
  setDialogSubTitle,
  setDialogContentType,
  setOpenDialog,
  setPaymentMethodId,
}: BankAccountFormProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { trackPayoutSetting } = useCustomEventTracking();
  const bankAccount = useAppSelector(selectBankAccount);
  const memberInfo = useAppSelector(selectMemberInformation);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [checkDefault, setCheckDefault] = useState(false);
  const [loadingOnSubmit, setLoadingOnSubmit] = useState(false);
  const logger = Logger.create('BankAccountForm');
  const dispatch = useAppDispatch();

  // For UNLINKED bank accounts, show empty form (same as adding new)
  const isUnlinked = bankAccount?.status === PaymentMethodStatus.UNLINKED;

  const defaultValues = {
    bankAccountInfo: {
      businessName: isUnlinked ? '' : (sanitizeString(bankAccount?.meta?.businessName) || ''),
      accountType: isUnlinked ?
        PaymentMethodBankAccountType.SAVINGS :
        (bankAccount?.meta?.accountType || PaymentMethodBankAccountType.SAVINGS),
      accountNumber: isUnlinked ? '' : (sanitizeString(bankAccount?.meta?.accountNumber) || ''),
      routingNumber: isUnlinked ? '' : (sanitizeString(bankAccount?.meta?.routingNumber) || ''),
      businessAddress: {
        street: isUnlinked ? '' : sanitizeString(bankAccount?.meta?.addressLine1),
        city: isUnlinked ? '' : sanitizeString(bankAccount?.meta.city),
        state: isUnlinked ? '' : sanitizeString(bankAccount?.meta.stateProvince),
        zipcode: isUnlinked ? '' : sanitizeString(bankAccount?.meta.postalCode),
        fullAddress: isUnlinked ? '' : getFullAddressUS(
          bankAccount?.meta?.addressLine1,
          bankAccount?.meta?.city,
          bankAccount?.meta?.stateProvince,
          bankAccount?.meta?.postalCode,
        ),
      },
    },
  };

  const methods = useForm<IBankAccountFormInputs>({
    defaultValues,
    mode: 'onChange',
  });

  const {
    getValues, handleSubmit, register,
    formState, clearErrors, getFieldState,
  } = methods;

  const { submitCount } = formState;
  const addressState = getFieldState('bankAccountInfo.businessAddress', formState);
  const addressFromDatabase = submitCount > 0 || addressState.isTouched || addressState.isDirty;

  useEffect(() => {
    if (bankAccount && bankAccount?.status === PaymentMethodStatus.VERIFIED) {
      setDialogSubTitle?.(`${t('wallet.payoutSettingsDialog.subTitle.manageBankAccount')}`);
    } else {
      setDialogSubTitle?.(`${t('wallet.payoutSettingsDialog.subTitle.connectNewBankAccount')}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSnackbarClose = () => {
    setErrorMessage('');
    setSnackbarOpen(false);
  };

  const handleRemoveAccount = () => {
    setDialogContentType?.(PayoutDialogContentType.REMOVE_ACCOUNT);
    const newSubTitle = (
      <>
        {t('wallet.payoutSettingsDialog.subTitle.delete')}
        <Typography
          variant='body4'
          component='span'
          sx={{
            [theme.breakpoints.down('sm')]: {
              fontSize: theme.spacing(2),
            },
          }}
        >
          {`Bank Account (${bankAccount?.meta?.accountNumber})?`}
        </Typography>
      </>
    );
    setPaymentMethodId?.(bankAccount?.id || '');
    setDialogSubTitle?.(newSubTitle);
  };

  const handleOnChange = () => {
    clearErrors('bankAccountInfo.accountType');
  };

  const onSubmit = async (data: IBankAccountFormInputs) => {
    if (data) {
      try {
        setLoadingOnSubmit(true);

        const request: CreateOrUpdatePaymentMethodACHInput = {
          type: 'bank_account',
          source: 'member',
          accountType: getValues('bankAccountInfo.accountType'),
          accountNumber: getValues('bankAccountInfo.accountNumber'),
          routingNumber: getValues('bankAccountInfo.routingNumber'),
          businessName: getValues('bankAccountInfo.businessName'),
          addressLine1: getValues('bankAccountInfo.businessAddress.street'),
          city: getValues('bankAccountInfo.businessAddress.city'),
          country: 'US',
          stateProvince: StateAbbreviationsUS[getValues('bankAccountInfo.businessAddress.state')],
          postalCode: getValues('bankAccountInfo.businessAddress.zipcode'),
        };

        // Using API call directly to NOT update any state and avoid close the dialog prematuraly
        const paymentMethod = await setPaymentMethod(memberInfo.id, request);
        const bankAccountResponse = paymentMethod.type === PaymentMethodType.BANK_ACCOUNT ?
          paymentMethod : undefined;

        // If successfull call, update states
        if (bankAccountResponse) {
          dispatch(UpdateBankAccountState(bankAccountResponse));
          setSnackbarOpen(false);
          setOpenDialog?.(false);
          trackPayoutSetting('Submit', 'BankAccountForm');
          if (checkDefault) {
            // TODO: move this api call out from setPaymentMethod response once fixed in BE
            await dispatch(activatePaymentMethod({ paymentMethodId: bankAccountResponse.id }))
              .then(() => trackPayoutSetting('Click', 'BankAccountDefault'))
              .catch((error: unknown) => logger.error(Logger.format(error)));
          }
          dispatch(getPaymentMethods());
        } else throw new Error('No bank account update');
      } catch (error) {
        setErrorMessage('Unable to Connect with Bank account. Please Review your Bank account details');
        setSnackbarOpen(true);
      } finally {
        setLoadingOnSubmit(false);
      }
    }
  };

  return (
    <>
      <FormProvider {...methods} >
        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}>
          <Backdrop
            sx={() => ({
              color: theme.palette.common.white, zIndex: () => theme.zIndex.drawer + 1,
            })}
            open={loadingOnSubmit}>
            <CircularProgress color="inherit" />
          </Backdrop>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: theme.spacing(3),
              alignSelf: 'stretch',
              width: '550px',
              [theme.breakpoints.down('sm')]: {
                width: 'auto',
              },
            }}
          >
            <FieldBox name={t('wallet.payoutSettingsDialog.bankTransfer.businessName')}>
              <>
                <StyledTextField
                  data-testid={TestIds.bankAccountForm.businessNameTxt}
                  placeholder='Name'
                  {...register('bankAccountInfo.businessName', { required: true })}
                />
                {(formState.errors?.bankAccountInfo?.businessName) && <ErrorAlert />}
              </>
            </FieldBox>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: theme.spacing(0.75),
                flex: ' 1 0 0',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: theme.spacing(0.25),
                }}>
                <Typography variant='h6'>
                  {t('wallet.payoutSettingsDialog.bankTransfer.accountType')}
                  <RequiredMark />
                </Typography>
              </Box>
              <StyledSelect
                data-testid={TestIds.bankAccountForm.accountTypeTxt}
                displayEmpty
                {...register('bankAccountInfo.accountType', { required: true })}
                onChange={(e) => { handleOnChange(); }}
                defaultValue={bankAccount?.meta?.accountType ||
                  PaymentMethodBankAccountType.SAVINGS}
              >
                <MenuItem disabled value=''>
                  {t('wallet.payoutSettingsDialog.bankTransfer.selectAccountTypePlaceholder')}
                </MenuItem>
                {Object.values(PaymentMethodBankAccountType).map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                  >
                    {option}
                  </MenuItem>
                ))}
              </StyledSelect>
              {(formState.errors?.bankAccountInfo?.accountType) && <ErrorAlert />}
            </Box>
            <FieldBox name={t('wallet.payoutSettingsDialog.bankTransfer.routingNumber')}>
              <>
                <StyledTextField
                  data-testid={TestIds.bankAccountForm.routingNumberTxt}
                  placeholder='Routing number'
                  {...register('bankAccountInfo.routingNumber', {
                    required: true,
                    pattern: {
                      value: numberLength9Regex,
                      message: 'Invalid Routing number',
                    },
                  })}
                />
                {(formState.errors?.bankAccountInfo?.routingNumber) && <ErrorAlert
                  errorMessage={formState.errors?.bankAccountInfo?.routingNumber?.message} />}
              </>
            </FieldBox>
            <FieldBox name={t('wallet.payoutSettingsDialog.bankTransfer.accountNumber')}>
              <>
                <StyledTextField
                  data-testid={TestIds.bankAccountForm.accountNumberTxt}
                  placeholder='Account number'
                  {...register('bankAccountInfo.accountNumber', {
                    required: true,
                    pattern: {
                      value: bankAccountNumberRegex,
                      message: 'Invalid Account number',
                    },
                  })}
                />
                {(formState.errors?.bankAccountInfo?.accountNumber) && <ErrorAlert
                  errorMessage={formState.errors?.bankAccountInfo?.accountNumber?.message} />}
              </>
            </FieldBox>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: theme.spacing(0.75),
                flex: ' 1 0 0',
              }}
            >
              <Typography variant='h6'>
                {t('wallet.payoutSettingsDialog.bankTransfer.businessAddress')}
                <RequiredMark />
              </Typography>
              <Box sx={{ width: '100%' }}>
                <AddressAutoCompleteInput
                  data-testid={TestIds.bankAccountForm.businessAddressTxt}
                  formFieldName={'bankAccountInfo.businessAddress'}
                  placeholderText={'Business address'}
                />
              </Box>
              {(formState.errors?.bankAccountInfo?.businessAddress) && <ErrorAlert
                errorMessage={
                  (addressFromDatabase) ?
                    `${t('error.addressField.required')}` :
                    `${t('error.addressField.confirm')}`
                }
              />}
            </Box>
            {(bankAccount && bankAccount?.status === PaymentMethodStatus.VERIFIED && !isUnlinked) ?
              <>
                <PayoutCTA text={t('button.updateBankAccount')}
                  data-testid={TestIds.bankAccountForm.connectBtn}
                  disable={!formState?.isValid || loadingOnSubmit} />
                <TransparentPayoutCTA
                  text={t('button.removeAccount')}
                  onClick={handleRemoveAccount}
                  disable={loadingOnSubmit}
                />
              </> : <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Checkbox
                      data-testid={TestIds.managePayoutSettings.activeCheckbox}
                      checked={checkDefault}
                      onChange={() => setCheckDefault(!checkDefault)}
                      sx={{
                        '&:hover': {
                          backgroundColor: theme.palette.brand.surface,
                        },
                        '& .MuiSvgIcon-root': {
                          color: theme.palette.grey[300],
                        },
                        '&.Mui-checked .MuiSvgIcon-root': {
                          color: theme.palette.brand.primary,
                        },
                      }}
                    />
                  </Box>
                  <Typography variant='body8' color={`${theme.palette.grey[800]}99`}>
                    {t('wallet.payoutSettingsDialog.setActive')}
                  </Typography>
                </Box>
                <PayoutCTA
                  data-testid={TestIds.bankAccountForm.connectBtn}
                  disable={!formState?.isValid || loadingOnSubmit}
                  text={t('button.connectBankAccount')} />
              </>
            }
          </Box>
        </form>
      </FormProvider>
      {/* Snackbar for Error Display */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
